import React, { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useInView, type Variants } from "framer-motion";
import {
  FEATURED_ARTICLE,
  GRID_ARTICLES,
  type ArticleMeta,
} from "../data/insights/insightsRegistry";

// ─── Animation ────────────────────────────────────────────────────────
const fadeUp: Variants = {
  hidden: { opacity: 0, y: 28 },
  show: (i: number = 0) => ({
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.65,
      ease: [0.22, 1, 0.36, 1] as const,
      delay: i * 0.07,
    },
  }),
};

function InView({
  children,
  delay = 0,
  className,
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" as any });
  return (
    <motion.div
      ref={ref}
      className={className}
      initial="hidden"
      animate={inView ? "show" : "hidden"}
      custom={delay}
      variants={fadeUp}
    >
      {children}
    </motion.div>
  );
}

// ─── Derived filter categories from the registry ─────────────────────
// Categories are built dynamically so adding a new tag in any JSON file
// automatically adds it to the filter strip — no manual update needed.
const ALL_TAGS = [
  "All",
  ...Array.from(new Set(GRID_ARTICLES.map((a) => a.tag))).sort(),
];

// ─── Article card ─────────────────────────────────────────────────────
function ArticleCard({
  article,
  delay,
}: {
  article: ArticleMeta;
  delay: number;
}) {
  return (
    <InView delay={delay} className="bg-white group">
      <Link to={article.href} className="block h-full">
        <div className="relative overflow-hidden aspect-[3/2]">
          <img
            src={article.image}
            alt={article.title}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
        </div>
        <div className="p-7">
          <div className="flex items-center gap-3 mb-4">
            <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-itl-blue">
              {article.tag}
            </span>
            <span className="text-gray-300 text-xs">/</span>
            <span className="text-gray-400 text-[11px]">{article.date}</span>
          </div>
          <h3 className="font-serif text-[18px] font-light text-itl-navy leading-snug mb-3 group-hover:text-itl-blue transition-colors">
            {article.title}
          </h3>
          <p className="text-gray-500 text-[13px] leading-relaxed line-clamp-3">
            {article.excerpt}
          </p>
          <p className="mt-5 text-[11px] font-semibold uppercase tracking-widest text-itl-blue flex items-center gap-1.5">
            Read{" "}
            <span className="group-hover:translate-x-1 transition-transform inline-block">
              →
            </span>
          </p>
        </div>
      </Link>
    </InView>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────
export default function Insights() {
  const [activeTag, setActiveTag] = useState("All");

  const filtered =
    activeTag === "All"
      ? GRID_ARTICLES
      : GRID_ARTICLES.filter((a) => a.tag === activeTag);

  return (
    <>
      {/* ── Page hero ── */}
      <div className="bg-itl-navy pt-40 pb-20">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-12">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="text-[11px] font-semibold uppercase tracking-[0.25em] text-itl-blue mb-4"
          >
            Insights &amp; Thinking
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
            className="font-serif text-5xl lg:text-6xl font-light text-white max-w-2xl leading-tight"
          >
            Ideas that move African tech forward.
          </motion.h1>
        </div>
      </div>

      {/* ── Featured article ── */}
      <section className="bg-white py-16">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-12">
          <InView>
            <Link
              to={FEATURED_ARTICLE.href}
              className="group grid grid-cols-1 lg:grid-cols-2 gap-0 border border-gray-100 hover:shadow-xl transition-shadow duration-300"
            >
              <div className="relative overflow-hidden aspect-[4/3] lg:aspect-auto">
                <img
                  src={FEATURED_ARTICLE.image}
                  alt={FEATURED_ARTICLE.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
              </div>
              <div className="p-10 lg:p-14 flex flex-col justify-center">
                <div className="flex items-center gap-3 mb-5">
                  <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-itl-blue">
                    {FEATURED_ARTICLE.tag}
                  </span>
                  <span className="text-gray-300">/</span>
                  <span className="text-gray-400 text-[11px]">
                    {FEATURED_ARTICLE.date}
                  </span>
                </div>
                <h2 className="font-serif text-3xl lg:text-4xl font-light text-itl-navy leading-snug mb-4 group-hover:text-itl-blue transition-colors">
                  {FEATURED_ARTICLE.title}
                </h2>
                <p className="text-gray-500 leading-relaxed mb-6">
                  {FEATURED_ARTICLE.excerpt}
                </p>
                <p className="text-[11px] font-semibold uppercase tracking-widest text-itl-blue flex items-center gap-2">
                  Read article{" "}
                  <span className="group-hover:translate-x-1 transition-transform inline-block">
                    →
                  </span>
                </p>
              </div>
            </Link>
          </InView>
        </div>
      </section>

      {/* ── Filter tabs ── */}
      {/* Tags are derived automatically from the registry — no manual update needed */}
      <section className="bg-[#f7f7f5] border-t border-gray-200 sticky top-[72px] z-40">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-12">
          <div className="flex items-center gap-1 overflow-x-auto py-4 scrollbar-hide">
            {ALL_TAGS.map((tag) => (
              <button
                key={tag}
                onClick={() => setActiveTag(tag)}
                className={`shrink-0 px-4 py-2 text-[11px] font-semibold uppercase tracking-widest transition-colors duration-200 ${
                  activeTag === tag
                    ? "bg-itl-navy text-white"
                    : "text-gray-500 hover:text-itl-navy"
                }`}
              >
                {tag}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ── Articles grid ── */}
      <section className="bg-[#f7f7f5] py-16">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-gray-200">
            {filtered.map((article, i) => (
              <ArticleCard
                key={article.slug}
                article={article}
                delay={i * 0.06}
              />
            ))}
          </div>

          {filtered.length === 0 && (
            <p className="text-center text-gray-400 py-20 text-sm">
              No articles in this category yet.
            </p>
          )}
        </div>
      </section>
    </>
  );
}
